import { useEffect, useState } from "preact/hooks";
import type { Manifest } from "../../shared/snapshot-format";
import { getSetting, listCharacters, type Settings } from "../character";
import { loadManifest } from "../data/manifest";
import { BUILD_ID, checkNow, currentState } from "../pwa/launch";
import type { PwaState } from "../pwa/state";
import { storageFacts, type StorageFacts } from "../pwa/storage";
import { formatBytes } from "../pwa/update";
import { SyncCard } from "./sync-card";
import { useUpdateStatus } from "./update-indicator";

/**
 * The About section of the home screen (issue #3): the Snapshot and app build in use, what is
 * installed for offline use, whether storage is persistent and how much is used, the update (its
 * state, a check, and "apply now", a relaunch through the gate, disabled while any Session is
 * open), and Sync once this Device is signed in (#26).
 */
export function About() {
  const [manifest, setManifest] = useState<Manifest>();
  const [error, setError] = useState<string>();
  const [state, setState] = useState<PwaState | null | undefined>();
  const [persist, setPersist] = useState<Settings["persist"]>(null);
  const [facts, setFacts] = useState<StorageFacts>();
  const [inPlay, setInPlay] = useState<string[]>([]);
  const status = useUpdateStatus();

  const refresh = () => {
    currentState().then(setState, () => setState(null));
    storageFacts().then(setFacts, () => undefined);
    getSetting("persist").then(setPersist, () => undefined);
    listCharacters().then((l) => setInPlay(l.filter((s) => s.sessionOpen).map((s) => s.name)), () => undefined);
  };
  useEffect(() => {
    loadManifest().then(setManifest, (e: unknown) => setError(String(e)));
    refresh();
  }, []);
  useEffect(refresh, [status.phase]);

  const staged = state?.staged ?? null;
  const applyBlocked = inPlay.length > 0 ? `${inPlay.join(", ")} ${inPlay.length === 1 ? "has" : "have"} a Session open; close it to apply.` : null;
  const busy = status.phase === "checking" || status.phase === "downloading";

  return (
    <section id="about" class="about">
      <h2>About</h2>
      <div class="card">
        {error ? (
          <p class="muted">Snapshot unavailable: {error}</p>
        ) : !manifest ? (
          <p class="muted">Loading Snapshot…</p>
        ) : (
          <>
            <p>
              <strong>Snapshot {manifest.snapshot.release}</strong> <span class="muted">(system {manifest.snapshot.systemVersion}, Foundry {manifest.snapshot.foundryVersion})</span>
            </p>
            <p class="muted">
              {manifest.packs.length} Packs, {manifest.packs.reduce((s, p) => s + p.count, 0).toLocaleString()} Entries, {formatBytes(manifest.totalBytes)}. Built {manifest.builtAt}. App {__APP_VERSION__}, build {BUILD_ID}.
            </p>
            <details>
              <summary>Packs</summary>
              <div class="table-scroll">
                <table>
                  <thead>
                    <tr><th>Pack</th><th class="num">Entries</th><th class="num">Shards</th></tr>
                  </thead>
                  <tbody>
                    {manifest.packs.map((p) => (
                      <tr key={p.id}><td>{p.label}</td><td class="num">{p.count}</td><td class="num">{p.shards.length}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        )}
      </div>

      <h3>Offline</h3>
      <div class="card">
        {state === undefined ? (
          <p class="muted">Reading…</p>
        ) : state === null ? (
          <p class="muted">This browser cannot install the app for offline use (no service worker or cache storage); every Pack is read from the network.</p>
        ) : state.active ? (
          <p>
            Installed: <strong>{state.active.release}</strong> <span class="muted">({formatBytes(state.active.totalBytes)} in {state.active.files} files, build {state.active.build}, since {state.active.at.slice(0, 10)})</span>
          </p>
        ) : (
          <p class="muted">Not installed yet.</p>
        )}
        <p>
          Persistent storage:{" "}
          <strong>{persist ? (persist.result === "granted" ? "granted" : persist.result === "denied" ? "not granted" : "not supported") : "not requested yet"}</strong>
          {persist ? <span class="muted"> (asked once, {persist.at.slice(0, 10)})</span> : null}
          {facts?.persisted !== null && facts?.persisted !== undefined ? <span class="muted">; the browser reports {facts.persisted ? "persisted" : "not persisted"} now</span> : null}
          .
        </p>
        {facts && facts.usage !== null ? (
          <p class="muted">
            Storage used: {formatBytes(facts.usage)}{facts.quota !== null ? ` of ${formatBytes(facts.quota)} available` : ""}.
          </p>
        ) : null}
      </div>

      <h3>Updates</h3>
      <div class="card">
        <p role="status">{describeStatus(status, staged)}</p>
        {status.phase === "downloading" && (
          <progress max={status.progress.bytes} value={status.progress.done} aria-label="Update download" />
        )}
        {staged && applyBlocked && <p class="muted">{applyBlocked}</p>}
        <span class="actions">
          <button type="button" disabled={busy || state === null} onClick={() => void checkNow()}>Check for updates</button>
          {staged && (
            <button type="button" class="primary" disabled={!!applyBlocked || busy} onClick={() => location.reload()}>
              Apply now
            </button>
          )}
        </span>
      </div>

      <SyncCard />
    </section>
  );
}

function describeStatus(status: ReturnType<typeof useUpdateStatus>, staged: PwaState["staged"]): string {
  switch (status.phase) {
    case "unsupported":
      return "Updates need a service worker; this browser has none.";
    case "checking":
      return "Checking for a newer build…";
    case "downloading":
      return `Downloading ${status.release || "the update"}: ${formatBytes(status.progress.done)} of ${formatBytes(status.progress.bytes)}.`;
    case "ready":
      return `Update to ${status.staged.release} (build ${status.staged.build}) is ready; it applies on the next launch with no Session open.${status.reason ? ` ${status.reason}` : ""}`;
    case "shell-pending":
      return `The Snapshot for ${status.release} is downloaded; the new app files are still on their way.`;
    case "current":
      return `Up to date (checked ${status.checkedAt.slice(11, 16)}).`;
    case "offline":
      return `Offline; could not check for updates (${status.checkedAt.slice(11, 16)}).`;
    case "failed":
      return `The update check failed: ${status.error}`;
    default:
      return staged ? `Update to ${staged.release} is ready; it applies on the next launch with no Session open.` : "Checks for a newer build a few seconds after launch.";
  }
}
